import Phaser from "phaser";
import type { WorldState } from "@/core";
import { TILE_SIZE } from "./MapScene";
import type { GameApp } from "../app/GameApp";

const MINIMAP_SIZE = 160;
const MINIMAP_MARGIN = 16;

export class MinimapScene extends Phaser.Scene {
  private app!: GameApp;
  private graphics!: Phaser.GameObjects.Graphics;
  private viewportRect!: Phaser.GameObjects.Graphics;
  private container!: Phaser.GameObjects.Container;
  private unsub?: () => void;
  private resizeHandler?: () => void;
  private isDragging = false;
  private mapTileSize = 2;

  constructor() {
    super({ key: "MinimapScene" });
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;

    this.isDragging = false;

    this.container = this.add.container(0, 0);

    const bg = this.add.graphics();
    bg.fillStyle(0x1c1a26, 0.97);
    bg.fillRoundedRect(0, 0, MINIMAP_SIZE + 8, MINIMAP_SIZE + 8, 8);
    bg.lineStyle(2, 0xc8a04a, 0.9);
    bg.strokeRoundedRect(0, 0, MINIMAP_SIZE + 8, MINIMAP_SIZE + 8, 8);
    bg.lineStyle(1, 0x3a3548, 0.6);
    bg.strokeRoundedRect(3, 3, MINIMAP_SIZE + 2, MINIMAP_SIZE + 2, 5);
    this.container.add(bg);

    this.graphics = this.add.graphics();
    this.graphics.setPosition(4, 4);
    this.container.add(this.graphics);

    this.viewportRect = this.add.graphics();
    this.viewportRect.setPosition(4, 4);
    this.container.add(this.viewportRect);

    const hitArea = new Phaser.Geom.Rectangle(0, 0, MINIMAP_SIZE + 8, MINIMAP_SIZE + 8);
    this.container.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
    this.container.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.navigateToMinimapPoint(pointer);
    });
    this.container.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        this.navigateToMinimapPoint(pointer);
      }
    });
    this.container.on("pointerup", () => {
      this.isDragging = false;
    });
    this.container.on("pointerout", () => {
      this.isDragging = false;
    });

    this.unsub = this.app.onWorldState((s) => this.render(s));
    const s0 = this.app.getWorldState();
    if (s0) this.render(s0);

    this.updatePosition();
    this.resizeHandler = () => this.updatePosition();
    this.scale.on("resize", this.resizeHandler);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private cleanup() {
    this.unsub?.();
    this.unsub = undefined;
    if (this.resizeHandler) {
      this.scale.off("resize", this.resizeHandler);
      this.resizeHandler = undefined;
    }
  }

  shutdown() {
    this.cleanup();
  }

  private updatePosition() {
    const w = this.scale.width;
    const h = this.scale.height;
    this.container.setPosition(w - MINIMAP_SIZE - MINIMAP_MARGIN - 8, h - MINIMAP_SIZE - MINIMAP_MARGIN - 8);
  }

  private navigateToMinimapPoint(pointer: Phaser.Input.Pointer) {
    const state = this.app.getWorldState();
    if (!state) return;

    const localX = pointer.x - this.container.x - 4;
    const localY = pointer.y - this.container.y - 4;

    const tileX = Math.floor(localX / this.mapTileSize);
    const tileY = Math.floor(localY / this.mapTileSize);

    this.game.events.emit("minimap:navigate", { tileX, tileY });
  }

  private render(state: WorldState) {
    const { width, height, tiles } = state.map;

    this.mapTileSize = Math.floor(MINIMAP_SIZE / Math.max(width, height));
    const ts = this.mapTileSize;

    this.graphics.clear();

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const tile = tiles[y][x];
        const px = x * ts;
        const py = y * ts;

        if (!tile.fog.discovered) {
          this.graphics.fillStyle(0x1e293b, 1);
        } else if (!tile.fog.visited) {
          this.graphics.fillStyle(0x475569, 1);
        } else {
          this.graphics.fillStyle(0x94a3b8, 1);
        }
        this.graphics.fillRect(px, py, ts, ts);

        if (tile.fog.discovered) {
          if (tile.content.kind === "town") {
            this.graphics.fillStyle(0x38bdf8, 1);
            this.graphics.fillRect(px + 1, py + 1, ts - 2, ts - 2);
          } else if (tile.content.kind === "event") {
            if ("eventId" in tile.content) {
              const ev = state.events.byId[tile.content.eventId];
              if (ev && !ev.triggered) {
                this.graphics.fillStyle(0xef4444, 1);
                this.graphics.fillCircle(px + ts / 2, py + ts / 2, Math.max(1, ts / 3));
              }
            } else {
              const hasUntriggered = tile.content.eventIds.some((eventId) => {
                const ev = state.events.byId[eventId]
                return ev && !ev.triggered
              })
              if (hasUntriggered) {
                this.graphics.fillStyle(0xef4444, 1);
                this.graphics.fillCircle(px + ts / 2, py + ts / 2, Math.max(1, ts / 3));
              }
            }
          } else if (tile.content.kind === "resource") {
            this.graphics.fillStyle(0xfbbf24, 1);
            this.graphics.fillCircle(px + ts / 2, py + ts / 2, Math.max(1, ts / 4));
          }
        }
      }
    }

    const heroPx = state.hero.x * ts + ts / 2;
    const heroPy = state.hero.y * ts + ts / 2;
    this.graphics.fillStyle(0x22c55e, 1);
    this.graphics.fillCircle(heroPx, heroPy, Math.max(2, ts / 2));

    this.updateViewportRect(state);
  }

  private updateViewportRect(_state: WorldState) {
    const mapScene = this.scene.get("MapScene") as Phaser.Scene;
    if (!mapScene || !mapScene.cameras) return;

    const cam = mapScene.cameras.main;
    if (!cam) return;

    const ts = this.mapTileSize;
    const tileSize = TILE_SIZE;

    const startTileX = Math.floor(cam.scrollX / tileSize);
    const startTileY = Math.floor(cam.scrollY / tileSize);
    const tilesInViewX = Math.ceil(cam.width / tileSize);
    const tilesInViewY = Math.ceil(cam.height / tileSize);

    this.viewportRect.clear();
    this.viewportRect.lineStyle(2, 0xfbbf24, 0.8);
    this.viewportRect.strokeRect(
      startTileX * ts,
      startTileY * ts,
      tilesInViewX * ts,
      tilesInViewY * ts
    );
  }

  update() {
    const state = this.app.getWorldState();
    if (state) {
      this.updateViewportRect(state);
    }
  }
}
